import { useState } from 'react';
import type { ReactElement } from 'react';
import { isValidPublisherName } from '@kirin/api-client/contracts/extension-validators';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2 } from 'lucide-react';
import { TruncatedBadge } from '@/components/ui/TruncatedBadge';

interface StringArrayInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  settingKey?: string;
  policyKey?: string;
  validation?: { pattern?: string; minLength?: number; maxLength?: number };
  disabled?: boolean;
}

function getExamplesSnippet(_settingKey?: string): ReactElement | null {
  return null;
}

export function StringArrayInput({
  value,
  onChange,
  placeholder,
  settingKey,
  policyKey,
  validation,
  disabled = false,
}: StringArrayInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const maxLength = settingKey === 'excludedPackages' ? 50 : 250;

  const validateInput = (input: string): boolean => {
    if (validation?.pattern) {
      try {
        const regex = new RegExp(validation.pattern);
        if (!regex.test(input)) {
          setError(`Must match pattern: ${validation.pattern}`);
          return false;
        }
      } catch (error) {
        // Show error for invalid regex pattern
        setError(
          `Invalid pattern: ${validation.pattern}. ${error instanceof Error ? error.message : 'Invalid regular expression'}`
        );
        return false;
      }
    }

    if (validation?.minLength && input.length < validation.minLength) {
      setError(`Minimum length: ${validation.minLength}`);
      return false;
    }

    if (
      policyKey === 'extensions-management' &&
      (settingKey === 'allowedPublishers' || settingKey === 'blockedPublishers')
    ) {
      const t = input.trim();
      if (t !== '' && !isValidPublisherName(t)) {
        setError('Invalid publisher name');
        return false;
      }
    }

    if (input.length > maxLength) {
      setError(`Maximum length: ${maxLength}`);
      return false;
    }

    setError('');
    return true;
  };

  const addItem = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed) && validateInput(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
      setError('');
    }
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            value={inputValue}
            onChange={e => {
              const newValue = e.target.value;
              setInputValue(newValue.slice(0, maxLength));
              validateInput(newValue.slice(0, maxLength));
            }}
            placeholder={placeholder}
            className={error ? 'border-red-500' : ''}
            maxLength={maxLength}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addItem();
              }
            }}
            disabled={disabled}
          />
          {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
        </div>
        <Button
          size="sm"
          type="button"
          onClick={addItem}
          disabled={!inputValue.trim() || !!error || disabled}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((item, index) => (
            <TruncatedBadge
              key={index}
              variant="secondary"
              rightContent={
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="ml-1 hover:text-destructive flex-shrink-0"
                  disabled={disabled}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              }
            >
              {item}
            </TruncatedBadge>
          ))}
        </div>
      )}
      {getExamplesSnippet(settingKey)}
    </div>
  );
}
